import house from "../assets/images/shutterstock_617467400-min.jpg";
import constants from "../constants/constants";
import { useState } from "react";
import SEO, { pageSEO } from "../components/seo.component";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../functions/animations";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartLine, faHouse, faKey } from "@fortawesome/free-solid-svg-icons";

export default function Evaluation() {
  const [popup, setPopup] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    propertyAddress: "",
    city: "",
    provinceState: "",
    propertySize: "",
    lotSize: "",
    propertyType: "",
    buildingType: "",
    notes: "",
  });

  const seoConfig = pageSEO.evaluation();

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  async function handleSubmit(event) {
    event.preventDefault();

    const submissionData = {
      ...formData,
      access_key: constants.EMAIL_ACCESS_KEY,
      subject: "New Lead Interested in Home Evaluation",
    };

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(submissionData),
      });

      const data = await res.json();

      setPopup(
        data?.success
          ? {
            subject: "Thank You!",
            message:
              "Your evaluation request has been received. We will contact you shortly.",
          }
          : {
            subject: "Something went wrong",
            message: "Please try again later.",
          }
      );
    } catch {
      setPopup({
        subject: "Something went wrong",
        message: "Please try again later.",
      });
    }
  }

  return (
    <>
      <SEO {...seoConfig} />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#faf8f4]">
        <div className="absolute right-0 top-0 h-[600px] w-[600px] rounded-full bg-[#e7dfd3] blur-[140px]" />
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mx-auto max-w-[1400px] px-6 pt-40 pb-20 lg:px-16"
        >
          <motion.p variants={fadeUp} className="uppercase tracking-[0.5em] text-sm text-gray-600">
            Complimentary Service
          </motion.p>
          <motion.h1 variants={fadeUp} className="mt-6 font-heading2 text-6xl leading-none text-neutral-800 lg:text-7xl">
            Discover Your
            <br />
            Home's True Value
          </motion.h1>
          <motion.p variants={fadeUp} className="mt-8 max-w-2xl text-lg leading-9 text-gray-700">
            Receive a personalized property evaluation based on current market trends, comparable homes, and expert local knowledge.
          </motion.p>
        </motion.div>
      </section>

      {/* CONTENT */}
      <section className="bg-[#fffdfa] py-28">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-16">
          <div className="grid gap-16 lg:grid-cols-[400px_1fr]">
            {/* IMAGE PANEL */}
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              <div className="sticky top-28">
                <div className="relative">
                  <div
                    className="absolute -left-8 -top-8 hidden h-full w-full border border-accent/40 lg:block" />

                  <div
                    className="relative overflow-hidden shadow-lg"
                  >
                    <img
                      src={house}
                      alt="Home Evaluation"
                      className="h-[500px] w-full object-cover lg:h-[650px]"
                    />
                    <div className="absolute bottom-0 left-0 bg-white/90 px-8 py-6 backdrop-blur-md">
                      <p
                        className="text-xs uppercase tracking-[0.35em] text-gray-600">
                        Home Evaluation
                      </p>
                      <p
                        className="mt-2 font-serif text-2xl text-gray-800">
                        Know Your Worth
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* FORM */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <div
                className="border border-accent/40 bg-white p-6 shadow-lg md:p-12"
              >
                <div
                  className="mb-10">
                  <p
                    className="text-xs uppercase tracking-[0.4em] text-accent">
                    Property Analysis
                  </p>
                  <h2
                    className="mt-5 font-serif text-4xl text-gray-800">
                    Request Your Evaluation
                  </h2>
                  <p
                    className="mt-4 leading-8 text-gray-600">
                    Complete the form below and receive your complimentary home evaluation with professional market insights.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <input
                    required
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Full Name"
                    className="w-full border border-gray-200 p-4 outline-none focus:border-accent/50"
                  />

                  <div className="grid gap-5 md:grid-cols-2">
                    <input
                      required
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Email Address"
                      className="w-full border border-gray-200 p-4 outline-none focus:border-accent/50"
                    />

                    <input
                      required
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Phone Number"
                      className="w-full border border-gray-200 p-4 outline-none focus:border-accent/50"
                    />
                  </div>

                  <input
                    required
                    name="propertyAddress"
                    value={formData.propertyAddress}
                    onChange={handleInputChange}
                    placeholder="Property Address"
                    className="w-full border border-gray-200 p-4 outline-none focus:border-accent/50"
                  />

                  <div className="grid gap-5 md:grid-cols-2">
                    <input
                      required
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder="City"
                      className="w-full border border-gray-200 p-4 outline-none focus:border-accent/50"
                    />

                    <input
                      required
                      name="provinceState"
                      value={formData.provinceState}
                      onChange={handleInputChange}
                      placeholder="Province / State"
                      className="w-full border border-gray-200 p-4 outline-none focus:border-accent/50"
                    />
                  </div>

                  <div className="grid gap-5 md:grid-cols-2">
                    <input
                      required
                      name="propertySize"
                      value={formData.propertySize}
                      onChange={handleInputChange}
                      placeholder="Property Size"
                      className="w-full border border-gray-200 p-4 outline-none focus:border-accent/50"
                    />

                    <input
                      required
                      name="lotSize"
                      value={formData.lotSize}
                      onChange={handleInputChange}
                      placeholder="Lot Size"
                      className="w-full border border-gray-200 p-4 outline-none focus:border-accent/50"
                    />
                  </div>

                  <select
                    required
                    name="propertyType"
                    value={formData.propertyType}
                    onChange={handleInputChange}
                    className="w-full border border-gray-200 p-4 outline-none focus:border-accent/50"
                  >
                    <option value="">Property Type</option>
                    <option>Residential</option>
                    <option>Commercial</option>
                    <option>Land</option>
                    <option>Investment</option>
                  </select>

                  <select
                    required
                    name="buildingType"
                    value={formData.buildingType}
                    onChange={handleInputChange}
                    className="w-full border border-gray-200 p-4 outline-none focus:border-accent/50"
                  >
                    <option value="">Building Type</option>
                    <option>House</option>
                    <option>Townhouse</option>
                    <option>Condo</option>
                    <option>Duplex</option>
                  </select>

                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Additional Information"
                    className="w-full border border-gray-200 p-4 outline-none focus:border-accent/50 h-32 resize-none"
                  />

                  <button className="btn-primary-outline-accent">
                    Request Evaluation
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* POPUP */}
      {popup && (
        <>
          <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" />

          <div className="
        fixed
        left-1/2
        top-1/2
        z-50
        w-[90%]
        max-w-md
        -translate-x-1/2
        -translate-y-1/2
        bg-white
        p-10
        text-center
        shadow-2xl
      ">

            <h3 className="font-serif text-3xl text-[#2b2925]">
              {popup.subject}
            </h3>

            <p className="mt-4 leading-8 text-[#5f594f]">
              {popup.message}
            </p>

            <button
              onClick={() => setPopup(false)}
              className="
            mt-8
            border
            border-[#2b2925]
            px-8
            py-3
            text-xs
            uppercase
            tracking-[0.3em]
            text-[#2b2925]
            hover:bg-[#2b2925]
            hover:text-white
            transition
          "
            >
              Close
            </button>
          </div>
        </>
      )}

    </>
  );
}